import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { fetchREOs, fetchVOLs } from '../../services/loanSubCollectionApi';
import US_STATES from '../../data/usStates';
import { lookupZipResults } from '../../utils/zipAutoPopulate';
import ZipCityPopup from '../shared/ZipCityPopup';
import { getAccessToken } from '../../services/authApi';

const DEFAULT_REO_COUNT = 7;
const MAX_REO_LIMIT = 25;

const defaultREO = {
    id: null,
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    lienHolder: [],
    propertyUsageType: '',
    gsePropertyType: '',
    marketValueAmount: '',
    maintenanceExpenseAmount: '',
    foreignAddressIndicator: false,
    country: 'US',
    subjectIndicator: false
};

const REOInformation = forwardRef(({ token, loanid, applicationId, onREOUpdate }, ref) => {
    const [noOfREO, setNoOfROE] = useState(DEFAULT_REO_COUNT);
    const [reoList, setReoList] = useState(Array(noOfREO).fill(0).map(() => ({ ...defaultREO })));

    const [availableVOLs, setAvailableVOLs] = useState({ all: [], assignedMap: {}, previousAssignments: {} });
    const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
    const [zipPopup, setZipPopup] = useState({ results: [], cityFieldId: '', stateFieldId: '' });

    const loadREOData = async () => {
        try {
            const [reoData, volData] = await Promise.all([
                fetchREOs(token, loanid, applicationId),
                fetchVOLs(token, loanid, applicationId)
            ]);

            const mortgageVols = volData.filter(v =>
                v.holderName
            );

            const assignedVolsMap = {};
            mortgageVols.forEach(vol => {
                if (vol.reoProperty?.entityId) {
                    assignedVolsMap[vol.id] = vol.reoProperty.entityId;
                }
            });

            // Create a map to track which VOL is tied to which REO
            const assignedHolderMap = {};
            mortgageVols.forEach(v => {
                if (v.reoProperty?.entityId) {
                    assignedHolderMap[v.id] = v.reoProperty.entityId;
                }
            });


            // REOs with their lienHolder (array of holder names)
            const matchedREOs = reoData.map(reo => {
                const assigned = mortgageVols.filter(
                    v => v.reoProperty?.entityId === reo.id
                );
                return {
                    ...reo,
                    lienHolderIds: assigned.map(v => v.id) || [],
                    foreignAddressIndicator: !!reo.foreignAddressIndicator,
                    country: reo.country || (reo.foreignAddressIndicator ? '' : 'US'),
                    subjectIndicator: !!reo.subjectIndicator
                };
            });

            const paddedList = (DEFAULT_REO_COUNT > matchedREOs.length) ? [
                ...matchedREOs,
                ...Array(DEFAULT_REO_COUNT - (matchedREOs.length)).fill(null).map(() => ({
                    ...defaultREO,
                    lienHolder: []
                }))
            ] : matchedREOs;

            setReoList(paddedList);
            setNoOfROE(paddedList.length);

            // Store all mortgageVols and assignment map for rendering
            setAvailableVOLs({
                all: mortgageVols,
                assignedMap: assignedHolderMap,
                previousAssignments: assignedVolsMap
            });

        } catch (error) {
            console.error("Error loading REO Information:", error.message);
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return '';
        const num = Number(value);
        if (isNaN(num)) return '';
        return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    };

    const renderStateField = (reo, idx) => {
        const isForeign = !!reo.foreignAddressIndicator;

        if (isForeign) {
            return (
                <input
                    type="text"
                    className="form-input w-full"
                    id={getFieldId('state', idx)}
                    value={reo.state || ''}
                    onChange={(e) => handleChange(idx, 'state', e.target.value)}
                />
            );
        }

        return (
            <select
                className="form-input w-full text-sm border rounded p-1"
                id={getFieldId('state', idx)}
                value={reo.state || ''}
                onChange={(e) => handleChange(idx, 'state', e.target.value)}
            >
                <option value="">Select State</option>
                {US_STATES.map((state) => (
                    <option key={state.abbrev} value={state.abbrev}>
                        {state.name}
                    </option>
                ))}
            </select>
        );
    };

    const renderCountryField = (reo, idx) => {
        const isForeign = !!reo.foreignAddressIndicator;
        const value = isForeign ? (reo.country || '') : 'US';

        return (
            <input
                type="text"
                className="form-input w-full"
                id={getFieldId('country', idx)}
                value={value}
                readOnly={!isForeign}
                onChange={(e) => {
                    if (isForeign) {
                        handleChange(idx, 'country', e.target.value);
                    }
                }}
            />
        );
    };


    useImperativeHandle(ref, () => ({
        refreshREO: loadREOData
    }));

    useEffect(() => {
        if (!token || !loanid || !applicationId) return;

        loadREOData();
    }, [token, loanid, applicationId]);

    useEffect(() => {
        setReoList(prev => {
            if (noOfREO > prev.length) {
                const newItems = Array(noOfREO - prev.length).fill(0).map(() => ({ ...defaultREO }));
                return [...prev, ...newItems];
            }
            return prev;
        });
    }, [noOfREO]);

    const handleAddREO = () => {
        if (noOfREO < MAX_REO_LIMIT) {
            setNoOfROE(noOfREO + 1);
        }
    };

    const handleChange = (index, field, value) => {
        const updated = [...reoList];

        if (field === 'lienHolder') {
            updated[index][field] = Array.isArray(value) ? value : [];
        } else {
            updated[index][field] = value;
        }

        if (!Array.isArray(updated[index].lienHolderIds)) {
            updated[index].lienHolderIds = [];
        }

        if (field === 'foreignAddressIndicator') {
            const isForeign = !!value;
            updated[index].foreignAddressIndicator = isForeign;

            if (isForeign) {
                if ((updated[index].country || '').toUpperCase() === 'US') {
                    updated[index].country = '';
                }
            } else {
                updated[index].country = 'US';
            }
        }

        if (field === 'lienHolderIds') {
            const selectedIds = Array.isArray(value) ? value : [];
            const vols = (availableVOLs?.all || []);

            let sumUnpaid = 0;
            let sumMonthly = 0;

            selectedIds.forEach(id => {
                const vol = vols.find(v => v.id === id);
                if (vol) {
                    const unpaid = parseFloat(vol.unpaidBalanceAmount ?? vol.lienUpbAmount ?? 0) || 0;
                    const monthly = parseFloat(vol.monthlyPaymentAmount ?? 0) || 0;
                    sumUnpaid += unpaid;
                    sumMonthly += monthly;
                }
            });

            updated[index].lienHolderIds = selectedIds;
            updated[index].lienUpbAmount = sumUnpaid === 0 ? '' : Number(sumUnpaid.toFixed(2));
            updated[index].lienInstallmentAmount = sumMonthly === 0 ? '' : Number(sumMonthly.toFixed(2));

            const newMap = {};
            updated.forEach((reo, idx) => {
                if (Array.isArray(reo.lienHolderIds)) {
                    reo.lienHolderIds.forEach(id => {
                        newMap[id] = idx;
                    });
                }
            });

            setReoList(updated);

            setAvailableVOLs(prev => ({
                ...prev,
                assignedMap: newMap
            }));

            return;
        }

        setReoList(updated);
    };


    useEffect(() => {
        if (onREOUpdate) {
            onREOUpdate(reoList, availableVOLs);
        }
    }, [reoList, availableVOLs]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.lien-holder-dropdown')) {
                setOpenDropdownIndex(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fieldIdMap = {
        streetAddress: 'FM0150',
        unitNumber: 'FM0148',
        city: 'FM0106',
        state: 'FM0107',
        postalCode: 'FM0108',
        country: 'FM0157',
        foreignAddressIndicator: 'FM0158',
        lienHolder: 'FL0102',
        propertyUsageType: 'FM0141',
        gsePropertyType: 'FM0118',
        lienUpbAmount: 'FM0117',
        lienInstallmentAmount: 'FM0116'
    };

    const getFieldId = (key, rowIndex) => {
        const base = fieldIdMap[key];
        if (!base) return '';
        const prefix = base.slice(0, 2);
        const number = parseInt(base.slice(2), 10) + (rowIndex * 100);
        return `field-${prefix}${number.toString().padStart(4, '0')}`;
    };

    const handleZipBlurRow = async (rowIndex) => {
        try {
            if (reoList[rowIndex]?.foreignAddressIndicator) {
                return;
            }

            let activeToken = token;

            if (!activeToken) {
                const authObject = await window.elli.script.getObject("auth");
                const authCode = await authObject.createAuthCode();
                activeToken = await getAccessToken(authCode);
            }

            const zipFieldId = getFieldId('postalCode', rowIndex);
            const cityFieldId = getFieldId('city', rowIndex);
            const stateFieldId = getFieldId('state', rowIndex);

            const zipInput = document.getElementById(zipFieldId);
            const zip = zipInput?.value?.trim();
            if (!zip || zip.length < 5) return;

            const results = await lookupZipResults({ zip, token: activeToken });

            if (results.length === 1) {
                const { city, abbrev } = results[0];
                handleChange(rowIndex, 'city', city);
                handleChange(rowIndex, 'state', abbrev);
            } else if (results.length > 1) {
                setZipPopup({ results, cityFieldId, stateFieldId });
            }
        } catch (err) {
            console.error("[handleZipBlurRow] Error fetching ZIP data:", err);
        }
    };


    const extractRowIndex = (fieldId, key) => {
        const baseField = fieldIdMap[key];
        if (!baseField) {
            console.warn(`[EXTRACT ROW] Invalid field key: ${key}`);
            return null;
        }

        const prefix = baseField.slice(0, 2);
        const baseNumber = parseInt(baseField.slice(2), 10);

        const match = fieldId.match(new RegExp(`field-${prefix}(\\d{4})`));
        if (!match) {
            console.warn(`[EXTRACT ROW] Field ID format invalid: ${fieldId}`);
            return null;
        }

        const fullNumber = parseInt(match[1], 10);
        const rowIndex = Math.floor((fullNumber - baseNumber) / 100);

        console.log(`[EXTRACT ROW] Field Key: ${key}, Field ID: ${fieldId}, Row Index: ${rowIndex}`);

        return rowIndex;
    };


    return (
        <>
            <ZipCityPopup
                visible={zipPopup.results.length > 1}
                results={zipPopup.results}
                onSelect={(opt) => {
                    const { cityFieldId, stateFieldId } = zipPopup;

                    const rowIndex = extractRowIndex(cityFieldId, 'city');

                    console.log("ZIP Popup Selection:");
                    console.log("Selected City/State:", opt.city, opt.abbrev);
                    console.log("City Field ID:", cityFieldId);
                    console.log("State Field ID:", stateFieldId);
                    console.log("Extracted Row Index:", rowIndex);

                    if (rowIndex !== null) {
                        const cityInput = document.getElementById(cityFieldId);
                        const stateInput = document.getElementById(stateFieldId);

                        if (cityInput) {
                            cityInput.value = opt.city;
                            console.log(`Updated cityInput (${cityFieldId}) with: ${opt.city}`);
                        }

                        if (stateInput) {
                            stateInput.value = opt.abbrev;
                            console.log(`Updated stateInput (${stateFieldId}) with: ${opt.abbrev}`);
                        }

                        handleChange(rowIndex, 'city', opt.city);
                        handleChange(rowIndex, 'state', opt.abbrev);
                    }

                    setZipPopup({ results: [], cityFieldId: '', stateFieldId: '' });
                }}
                onClose={() => {
                    console.log("ZIP Popup Closed");
                    setZipPopup({ results: [], cityFieldId: '', stateFieldId: '' });
                }}
            />


            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] mb-3">
                <div className="section-header flex justify-between items-center">
                    <span>REO Information</span>
                    <AddREOButton
                        noOfREO={noOfREO}
                        onAddREO={handleAddREO}
                        maxLimit={MAX_REO_LIMIT}
                    />
                </div>
                <div className="p-4">
                    <p className="text-sm text-[var(--slate-grey-2)] font-medium mb-4">
                        <span className="font-bold">Schedule of Real Estate Owned</span>{' '}
                        (If additional properties are owned, use continuation sheet.)
                    </p>

                    {reoList.map((reo, idx) => (
                        <div key={idx} className="border rounded-md p-4 mb-3">
                            <div className="grid grid-cols-8 gap-4 mb-2">
                                <div className="col-span-1">
                                    <label className="form-label mb-1">Foreign Address</label>
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={getFieldId('foreignAddressIndicator', idx)}
                                            checked={!!reo.foreignAddressIndicator}
                                            disabled={!!reo.subjectIndicator}
                                            onChange={(e) => handleChange(idx, 'foreignAddressIndicator', e.target.checked)}
                                        />
                                        <label
                                            htmlFor={getFieldId('foreignAddressIndicator', idx)}
                                            className="ml-2 text-sm"
                                        >
                                            Yes
                                        </label>
                                    </div>
                                </div>
                                <div className="col-span-2">
                                    <label className="form-label mb-1">Property Address</label>
                                    <input type="text"
                                        className="form-input"
                                        id={getFieldId('streetAddress', idx)}
                                        value={reo.streetAddress || ''}
                                        onChange={(e) => handleChange(idx, 'streetAddress', e.target.value)}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="form-label mb-1">Unit</label>
                                    <input type="text"
                                        className="form-input w-full"
                                        id={getFieldId('unitNumber', idx)}
                                        value={reo.unitNumber || ''}
                                        onChange={(e) => handleChange(idx, 'unitNumber', e.target.value)}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="form-label mb-1">City</label>
                                    <input type="text"
                                        className="form-input w-full"
                                        id={getFieldId('city', idx)}
                                        value={reo.city || ''}
                                        onChange={(e) => handleChange(idx, 'city', e.target.value)}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="form-label mb-1">State</label>
                                    {renderStateField(reo, idx)}
                                </div>
                                <div className="col-span-1">
                                    <label className="form-label mb-1">Zip</label>
                                    <input
                                        type="text"
                                        className="form-input w-full"
                                        id={getFieldId('postalCode', idx)}
                                        value={reo.postalCode || ''}
                                        onChange={(e) => handleChange(idx, 'postalCode', e.target.value)}
                                        onBlur={() => handleZipBlurRow(idx)}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="form-label mb-1">Country</label>
                                    {renderCountryField(reo, idx)}
                                </div>
                            </div>
                            <div className="grid grid-cols-8 gap-4 items-center mb-2">
                                <div className="col-span-3 relative">
                                    <label className="form-label mb-1">Lien Holder</label>
                                    <div className="relative lien-holder-dropdown">
                                        <input
                                            type="text"
                                            className="form-input w-full bg-gray-100 cursor-pointer pr-8 appearance-none relative dropdown-arrow"
                                            id={getFieldId('lienHolder', idx)}
                                            value={(reo.lienHolderIds || [])
                                                .map(id => availableVOLs.all.find(v => v.id === id)?.holderName)
                                                .filter(Boolean)
                                                .join(', ')
                                            }
                                            onChange={() => { }}
                                            onClick={() => setOpenDropdownIndex(openDropdownIndex === idx ? null : idx)}
                                        />

                                        {openDropdownIndex === idx && (
                                            <div className="absolute z-50 bg-white border rounded shadow-md max-h-40 overflow-y-auto mt-1 w-full">
                                                {(() => {
                                                    const availableList = (availableVOLs?.all || [])
                                                        .filter(vol =>
                                                            availableVOLs.assignedMap[vol.id] === undefined ||
                                                            availableVOLs.assignedMap[vol.id] === idx ||
                                                            (reo.lienHolderIds || []).includes(vol.id)
                                                        );

                                                    if (availableList.length === 0) {
                                                        return (
                                                            <div className="px-3 py-2 text-sm text-gray-500 italic">
                                                                No lien holders available
                                                            </div>
                                                        );
                                                    }

                                                    return availableList.map(vol => {
                                                        const isChecked = (reo.lienHolderIds || []).includes(vol.id);
                                                        return (
                                                            <label key={vol.id} className="flex items-center px-2 py-1 hover:bg-gray-100 cursor-pointer text-sm">
                                                                <input
                                                                    type="checkbox"
                                                                    className="mr-2"
                                                                    checked={isChecked}
                                                                    onChange={(e) => {
                                                                        const currentList = new Set(reo.lienHolderIds || []);
                                                                        if (e.target.checked) {
                                                                            currentList.add(vol.id);
                                                                        } else {
                                                                            currentList.delete(vol.id);
                                                                        }
                                                                        handleChange(idx, 'lienHolderIds', Array.from(currentList));
                                                                    }}
                                                                />
                                                                <span className="ml-2">{vol.holderName}</span>
                                                            </label>
                                                        );
                                                    });
                                                })()}


                                            </div>
                                        )}
                                    </div>
                                </div>



                                <div className="col-span-2">
                                    <label className="form-label mb-1">Property is Used as</label>
                                    <select
                                        className="form-input  w-full text-sm border rounded p-1"
                                        id={getFieldId('propertyUsageType', idx)}
                                        value={reo.propertyUsageType || ''}
                                        onChange={(e) => handleChange(idx, 'propertyUsageType', e.target.value)}
                                    >
                                        <option value=""></option>
                                        <option value="PrimaryResidence">Primary Residence</option>
                                        <option value="SecondHome">Second Home</option>
                                        <option value="Investor">Investment Property</option>
                                        <option value="FHASecondaryResidence">FHA Secondary Residence</option>
                                    </select>
                                </div>

                                <div className="col-span-2">
                                    <label className="form-label mb-1">Type of Property</label>
                                    <select
                                        className="form-input w-full text-sm border rounded p-1"
                                        id={getFieldId('gsePropertyType', idx)}
                                        value={reo.gsePropertyType || ''}
                                        onChange={(e) => handleChange(idx, 'gsePropertyType', e.target.value)}
                                    >
                                        <option value=""></option>
                                        <option value="SingleFamily">Single Family</option>
                                        <option value="Condominium">Condominium</option>
                                        <option value="Townhouse">Townhouse</option>
                                        <option value="Cooperative">Cooperative</option>
                                        <option value="TwoToFourUnitProperty">2 to 4 Units</option>
                                        <option value="MultifamilyMoreThanFourUnits">Multi-Family+4</option>
                                        <option value="ManufacturedMobileHome">Mobile Home</option>
                                        <option value="CommercialNonResidential">Commercial</option>
                                        <option value="HomeAndBusinessCombined">Home And Business Combined</option>
                                        <option value="MixedUseResidential">Mixed Use</option>
                                        <option value="Farm">Farm</option>
                                        <option value="Land">Land</option>
                                    </select>
                                </div>

                            </div>
                            <div className="grid grid-cols-4 gap-4">
                                <div>
                                    <label className="form-label mb-1">Amount of Mortgages & Liens</label>
                                    <input type="text"
                                        className="form-input w-full"
                                        id={getFieldId('lienUpbAmount', idx)}
                                        value={formatCurrency(reo.lienUpbAmount)}
                                        onChange={(e) => handleChange(idx, 'lienUpbAmount', e.target.value)}
                                        readOnly
                                    />
                                </div>
                                <div>
                                    <label className="form-label mb-1">Mortgage Payments</label>
                                    <input type="text"
                                        className="form-input w-full"
                                        id={getFieldId('lienInstallmentAmount', idx)}
                                        value={formatCurrency(reo.lienInstallmentAmount)}
                                        onChange={(e) => handleChange(idx, 'lienInstallmentAmount', e.target.value)}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                </div>
                <div className="p-4 pt-0 !border-0 flex justify-end">
                    <AddREOButton
                        noOfREO={noOfREO}
                        onAddREO={handleAddREO}
                        maxLimit={MAX_REO_LIMIT}
                    />
                </div>
            </div>
        </>
    );
});

const AddREOButton = ({ noOfREO, onAddREO, maxLimit }) => {
    const isDisabled = noOfREO >= maxLimit;

    const handleClick = () => {
        if (!isDisabled) {
            onAddREO();
        }
    };

    return (
        <button
            className="py-2 px-3 bg-blue-500 text-white text-sm font-semibold rounded-md shadow focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleClick}
            disabled={isDisabled}
            title={isDisabled ? `Maximum ${maxLimit} REO items allowed` : 'Add a new REO property'}
        >
            Add REO
        </button>
    );
};

export default REOInformation;
